import React, { useState, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import './PaymentPopup.css';
import { Order } from '../interfaces';
import { formatPrice, getCurrencySymbol } from '../utils/formatPrice';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import SuccessNotification from './notifications/SuccessNotification';
import { useAuth } from '../AuthContext';

interface CashPaymentPopupProps {
    isOpen: boolean;
    onClose: () => void;
    activeOrder: Order | undefined;
    onPaymentComplete?: () => void;
    onPartialPayment?: () => void;
    seatTotal?: number;
    reservationInfo?: { reservationId: string; reservationNumber: string; roomNumber: string; roomWithStatusId: string; seatId?: string; guestName?: string };
}

const CashPaymentPopup: React.FC<CashPaymentPopupProps> = ({ isOpen, onClose, activeOrder, onPaymentComplete, onPartialPayment, seatTotal, reservationInfo }) => {
    const { hasPerm } = useAuth();
    const can = (key: string) => !hasPerm || hasPerm(key);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const [inputValue, setInputValue] = useState<string>('');
    const [order, setOrder] = useState(activeOrder);
    const [isLoading, setIsLoading] = useState(false);
    const [inputMode, setInputMode] = useState<'quick' | 'count' | 'keypad'>('quick');
    const [denomCounts, setDenomCounts] = useState<Record<string, number>>({});
    const [showSuccess, setShowSuccess] = useState(false);
    const navigate = useNavigate();
    const [headerTitle, setHeaderTitle] = useState('');
    const [content, setContent] = useState('');

    function generateGUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0,
                v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    const formatNumberWithCommas = (value: string) => {
        const cleanValue = value.replace(/,/g, '');
        const parts = cleanValue.split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const decimalPart = parts[1] ? `.${parts[1]}` : '';
        return integerPart + decimalPart;
    };

    const handleButtonClick = (char: string) => {
        if (char === '.') {
            if (!inputValue.includes('.')) {
                setInputValue(inputValue + '.');
            }
        } else {
            setInputValue(formatNumberWithCommas(inputValue + char));
        }
    };

    const handleOkClick = async () => {
        if (!can('pos-app.payment.cash')) return;
        const paymentAmount = parseFloat(inputValue.replace(/,/g, '') || "0");
        const change = parseFloat(getChange());
        const balance = parseFloat(getBalance());

        if (paymentAmount != 0) {
            const newPayment = {
                id: generateGUID(),
                amount: paymentAmount,
                type: "cash",
                date: new Date(),
                change: change,
                balanceBefore: balance,
                confirmationNumber: '',
                cardType: '',
                reservationId: reservationInfo?.reservationId || '',
                reservationNumber: reservationInfo?.reservationNumber || '',
                roomNumber: reservationInfo?.roomNumber || '',
                roomWithStatusId: reservationInfo?.roomWithStatusId || '',
                seatId: reservationInfo?.seatId || '',
                guestName: reservationInfo?.guestName || '',
            };

            if (order?.id) {
                const updatedOrder = {
                    ...order,
                    payments: [...(order?.payments || []), newPayment],
                };
                await saveOrder(updatedOrder);
            }
        }
    };
    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (!isOpen) return;

            const { key } = event;

            if (!isNaN(Number(key))) {
                setInputValue((prev) => formatNumberWithCommas(prev + key));
            } else if (key === '.' && !inputValue.includes('.')) {
                setInputValue((prev) => prev + '.');
            } else if (key === 'Backspace') {
                setInputValue((prev) => prev.slice(0, -1));
            } else if (key === 'Enter') {
                handleOkClick();
            }
        };

        window.addEventListener('keydown', handleKeyPress);

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, inputValue]); // `handleOkClick` is intentionally excluded

    const saveOrder = async (updatedOrder: Order) => {
        try {
            setIsLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: updatedOrder.id,
                    propertyId: updatedOrder.propertyId,
                    products: updatedOrder.products,
                    customerEmail: updatedOrder.customerEmail,
                    customerName: updatedOrder.customerName,
                    // This is the request that closes the order. Without the customer link and
                    // the seats, nothing here identifies who the order was for, so it cannot be
                    // recorded against their history.
                    customerId: updatedOrder.customerId,
                    seats: updatedOrder.seats,
                    currency: updatedOrder.currency,
                    subtotal: updatedOrder.subtotal,
                    total: updatedOrder.total,
                    tax: updatedOrder.tax,
                    isTaxPercentage: updatedOrder.isTaxPercentage,
                    discount: updatedOrder.discount,
                    isOpen: updatedOrder.isOpen,
                    tableId: updatedOrder.tableId,
                    tableName: updatedOrder.tableName,
                    orderDiscountType: updatedOrder.orderDiscountType,
                    orderDiscountValue: updatedOrder.orderDiscountValue,
                    orderDiscountName: updatedOrder.orderDiscountName,
                    status: updatedOrder.status,
                    orderNumber: updatedOrder.orderNumber,
                    waiter: updatedOrder.waiter,
                    date: updatedOrder.date,
                    closedBy: updatedOrder.closedBy,
                    createdBy: updatedOrder.createdBy,
                    orderPaidOn: updatedOrder.orderPaidOn,
                    isVoided: updatedOrder.isVoided,
                    combinations: updatedOrder.combinations,
                    subtotalWithoutDiscounts: updatedOrder.subtotalWithoutDiscounts,
                    payments: updatedOrder.payments,
                    roomNumber: updatedOrder.roomNumber || activeOrder?.roomNumber,
                    prostayReservationNumber: updatedOrder.prostayReservationNumber || activeOrder?.prostayReservationNumber,
                    orderRoomAllocations: updatedOrder.orderRoomAllocations || activeOrder?.orderRoomAllocations || [],
                }),
                credentials: 'include',
            });
            if (response) {
                setInputValue('');
                setOrder(updatedOrder);

                if (updatedOrder.payments && updatedOrder.payments.length > 0) {
                    const totalPaid = updatedOrder.payments.reduce((sum: number, p: any) => sum + (Number(p.amount) || 0) - (Number(p.change) || 0), 0);
                    const orderTotal = updatedOrder.total || 0;
                    const isFullyPaid = totalPaid >= orderTotal;

                    if (isFullyPaid) {
                        setHeaderTitle('Success!');
                        setContent('Order was closed successfully.');
                        setShowSuccess(true);
                        setTimeout(() => {
                            setShowSuccess(false);
                            if (onPaymentComplete) onPaymentComplete();
                            navigate('/orders');
                        }, 3000);
                    } else {
                        setHeaderTitle('Payment Recorded');
                        setContent(`Payment of ${formatPrice(Number(inputValue.replace(/,/g, '')))} recorded. Remaining: ${formatPrice(orderTotal - totalPaid)}`);
                        setShowSuccess(true);
                        setTimeout(() => {
                            setShowSuccess(false);
                            if (onPartialPayment) onPartialPayment();
                            else onClose();
                        }, 2000);
                    }
                }
            }
        } catch (error) {
            console.error(error);
        }
        finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (activeOrder) {
            console.log(activeOrder);
            setOrder(activeOrder);
        }
    }, [activeOrder]);

    const styles = StyleSheet.create({
        drawer: {
            top: 0,
            bottom: 0,
            right: 0,
            width: windowSize.width > 600 ? 600 : windowSize.width * 0.9,
            height: windowSize.height,
            backgroundColor: 'white',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: '#D0D5DD',
            elevation: 5,
            zIndex: 1002,
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
        },
        openDrawer: {
            right: 0,
        },
        closedDrawer: {
            right: -(windowSize.width > 900 ? windowSize.width * 0.47 : windowSize.width * 0.9),
        },
        orderProductsContainer: {
            maxHeight: windowSize.height - 460,
        },
        spinnerOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(256, 256, 256, 0.8)',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1100,
        },
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const formatDateTime = (date: Date | string) => {
        if (!(date instanceof Date)) {
            date = new Date(date);
        }

        const options: Intl.DateTimeFormatOptions = {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        };

        const formatter = new Intl.DateTimeFormat("en-GB", options);
        return formatter.format(date).replace(',', '');
    };

    const handleEraseClick = () => {
        if (inputValue != '') {
            setInputValue((prev) => prev.slice(0, -1));
        }
    };


    const getBalance = () => {
        if (!order) return "0.00";

        // For split bill (seatTotal set), the balance is just the seat amount — don't subtract other seats' payments
        if (seatTotal !== undefined) {
            return isNaN(seatTotal) ? "0.00" : seatTotal.toFixed(2);
        }

        const total = order.total || 0;
        const payments = order?.payments || [];
        const totalPayments = payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0) - (Number(payment.change) || 0), 0);
        const balance = (Number(total) || 0) - totalPayments;

        return isNaN(balance) ? "0.00" : balance.toFixed(2);
    }

    const getChange = () => {
        const balance = parseFloat(getBalance());
        const inputValueNumber = parseFloat(inputValue.replace(/,/g, '') || "0");

        if (inputValueNumber > balance) {
            return (inputValueNumber - balance).toFixed(2);
        }

        return "0.00";
    };

    // Amount still owed for this payment (seat split, partial payments, or full).
    const amountDue = (() => {
        const bal = parseFloat(getBalance());
        return isNaN(bal) ? 0 : Math.max(0, bal);
    })();

    // What the cashier has keyed/tapped as received, and the resulting change.
    const received = parseFloat(inputValue.replace(/,/g, '') || "0") || 0;
    const changeDue = received > amountDue ? received - amountDue : 0;
    const isSettled = received >= amountDue && amountDue > 0;

    // One-tap tender suggestions: exact balance, the next round notes and the
    // smallest standard banknotes that cover it. Mirrors how a cashier actually
    // takes cash — pick the note handed over instead of typing every digit.
    const getQuickTenders = (): number[] => {
        const due = amountDue;
        if (!due || due <= 0) return [];
        const candidates = new Set<number>();
        candidates.add(Number(due.toFixed(2)));        // exact
        candidates.add(Math.ceil(due / 5) * 5);        // next €5
        candidates.add(Math.ceil(due / 10) * 10);      // next €10
        [5, 10, 20, 50, 100, 200].forEach((n) => { if (n >= due) candidates.add(n); });
        return Array.from(candidates)
            .filter((v) => v >= due)
            .sort((a, b) => a - b)
            .slice(0, 5);
    };

    const tenderAmount = (amount: number) => {
        setDenomCounts({});
        setInputValue(formatNumberWithCommas(amount.toFixed(2)));
    };

    // Denomination counting: tally how many of each note/coin was handed over.
    const CASH_DENOMS = [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1];
    const denomLabel = (d: number) => `${getCurrencySymbol()}${d % 1 === 0 ? d : d.toFixed(2)}`;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const denomsTotal = CASH_DENOMS.reduce((sum, d) => sum + d * (denomCounts[String(d)] || 0), 0);
    const adjustDenom = (d: number, delta: number) => {
        setDenomCounts((prev) => {
            const key = String(d);
            const next = { ...prev };
            const v = Math.max(0, (next[key] || 0) + delta);
            if (v === 0) delete next[key]; else next[key] = v;
            return next;
        });
    };
    const chooseMode = (mode: 'quick' | 'count' | 'keypad') => {
        setInputMode(mode);
        if (mode !== 'count' && Object.keys(denomCounts).length > 0) setDenomCounts({});
        if (mode === 'quick') setInputValue('');
    };

    // Keep the received amount in sync with the counted notes/coins.
    useEffect(() => {
        if (inputMode !== 'count') return;
        const total = CASH_DENOMS.reduce((sum, dn) => sum + dn * (denomCounts[String(dn)] || 0), 0);
        setInputValue(total > 0 ? formatNumberWithCommas(total.toFixed(2)) : '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [denomCounts, inputMode]);

    return (
      <>
        <div className={`paypop-scrim${isOpen ? ' is-open' : ''}`} onClick={onClose} />
        <div className={`paypop ps-scope${isOpen ? ' is-open' : ''}`} style={{ ...styles.drawer, ...(isOpen ? styles.openDrawer : styles.closedDrawer) }}>
            <div className='cashpay'>
                <header className='cashpay__head'>
                    <div className='cashpay__head-id'>
                        <span className='cashpay__eyebrow'>Cash payment</span>
                        <span className='cashpay__table'>{order?.tableName}{activeOrder?.customerName ? ` · ${activeOrder?.customerName}` : ''}</span>
                    </div>
                    <button className='cashpay__close' onClick={onClose} aria-label='Close'>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </header>

                <div className='cashpay__body'>
                    {/* Hero — flips from amount due to change owed */}
                    <div className={`cashpay__hero${changeDue > 0 ? ' is-change' : ''}`}>
                        <span className='cashpay__hero-label'>{changeDue > 0 ? 'Change to give' : 'Amount due'}</span>
                        <span className='cashpay__hero-amt'>{formatPrice(changeDue > 0 ? changeDue : amountDue)}</span>
                        {received > 0 && (
                            <span className='cashpay__hero-sub'>
                                Received {formatPrice(received)} · Due {formatPrice(amountDue)}
                            </span>
                        )}
                    </div>

                    {/* Cash received — three swappable input modes, never stacked */}
                    <div className='cashpay__section'>
                        <div className='cashpay__section-head'>
                            <span className='cashpay__section-label'>Cash received</span>
                            <div className='cashpay__modes' role='tablist'>
                                <button type='button' className={`cashpay__mode${inputMode === 'quick' ? ' is-active' : ''}`} onClick={() => chooseMode('quick')}>Quick</button>
                                <button type='button' className={`cashpay__mode${inputMode === 'count' ? ' is-active' : ''}`} onClick={() => chooseMode('count')}>Count</button>
                                <button type='button' className={`cashpay__mode${inputMode === 'keypad' ? ' is-active' : ''}`} onClick={() => chooseMode('keypad')}>Keypad</button>
                            </div>
                        </div>

                        <div className='cashpay__region'>
                            {inputMode === 'quick' && (
                                <div className='cashpay__tenders'>
                                    {getQuickTenders().map((amount) => {
                                        const isExact = Math.abs(amount - amountDue) < 0.005;
                                        const isSelected = Math.abs(amount - received) < 0.005 && received > 0;
                                        return (
                                            <button
                                                key={amount}
                                                type='button'
                                                className={`cashpay__tender${isSelected ? ' is-selected' : ''}`}
                                                onClick={() => tenderAmount(amount)}
                                            >
                                                {isExact && <span className='cashpay__tender-tag'>Exact</span>}
                                                <span className='cashpay__tender-amt'>{getCurrencySymbol()}{amount.toFixed(2)}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {inputMode === 'count' && (
                                <div className='cashpay__denoms'>
                                    {CASH_DENOMS.map((d) => {
                                        const count = denomCounts[String(d)] || 0;
                                        return (
                                            <div key={d} className={`cashpay__denom${count > 0 ? ' is-filled' : ''}`}>
                                                <span className='cashpay__denom-face'>{denomLabel(d)}</span>
                                                <div className='cashpay__stepper'>
                                                    <button type='button' className='cashpay__step' onClick={() => adjustDenom(d, -1)} disabled={count === 0} aria-label={`Remove ${denomLabel(d)}`}>−</button>
                                                    <span className='cashpay__step-count'>{count}</span>
                                                    <button type='button' className='cashpay__step' onClick={() => adjustDenom(d, 1)} aria-label={`Add ${denomLabel(d)}`}>+</button>
                                                </div>
                                                <span className='cashpay__denom-sub'>{count > 0 ? `${getCurrencySymbol()}${(d * count).toFixed(2)}` : ''}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {inputMode === 'keypad' && (
                                <div className='cashpay__keypad'>
                                    <div className='cashpay__display'>
                                        <span className='cashpay__display-cur'>{getCurrencySymbol()}</span>
                                        <span className='cashpay__display-val'>{inputValue || '0'}</span>
                                    </div>
                                    <div className='cashpay__keys'>
                                        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'].map((char) => (
                                            <button key={char} type='button' className='cashpay__key' onClick={() => handleButtonClick(char)}>{char}</button>
                                        ))}
                                        <button type='button' className='cashpay__key cashpay__key--erase' onClick={handleEraseClick} aria-label='Backspace'>
                                            <svg width="22" height="20" viewBox="0 0 36 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M26.3333 9.49984L16.3333 19.4998M16.3333 9.49984L26.3333 19.4998M2.53333 16.0998L9.73333 25.6998C10.32 26.4821 10.6133 26.8732 10.9851 27.1553C11.3143 27.4051 11.6872 27.5915 12.0846 27.705C12.5333 27.8332 13.0222 27.8332 14 27.8332H26.6667C29.4669 27.8332 30.8671 27.8332 31.9366 27.2882C32.8774 26.8088 33.6423 26.0439 34.1217 25.1031C34.6667 24.0336 34.6667 22.6334 34.6667 19.8332V9.1665C34.6667 6.36624 34.6667 4.96611 34.1217 3.89655C33.6423 2.95574 32.8774 2.19084 31.9366 1.71147C30.8671 1.1665 29.4669 1.1665 26.6667 1.1665H14C13.0222 1.1665 12.5333 1.1665 12.0846 1.29466C11.6872 1.40816 11.3143 1.59458 10.9851 1.84442C10.6133 2.12651 10.32 2.51762 9.73333 3.29984L2.53333 12.8998C2.10301 13.4736 1.88785 13.7605 1.80491 14.0756C1.7317 14.3537 1.7317 14.646 1.80491 14.9241C1.88785 15.2392 2.10301 15.5261 2.53333 16.0998Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <footer className='cashpay__foot'>
                    <button
                        type='button'
                        className='cashpay__confirm'
                        disabled={!isSettled}
                        onClick={handleOkClick}
                    >
                        {isSettled
                            ? <>Complete payment · {formatPrice(received)}</>
                            : received > 0
                                ? <>Need {formatPrice(amountDue - received)} more</>
                                : <>Select cash received</>}
                    </button>
                </footer>
            </div>

            {isLoading && (
                <div>
                    <div style={styles.spinnerOverlay}></div>
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1101,
                        }}
                    >
                        <CircularProgress
                            style={{
                                zIndex: 1102,
                            }}
                        />
                    </div>
                </div>
            )}

            {showSuccess && <SuccessNotification headerTitle={headerTitle} content={content} />}
        </div>
      </>
    );
};

export default CashPaymentPopup;

